import type { LandingPageBody, LandingPageParams } from "@apptus/esales-api";
import { useMarket } from "@features/market/useMarket";
import getLandingPage from "@server/requests/elevate/getLandingPage";
import mapNgFilters from "@server/requests/elevate/mapNgFilters";
import mapNgSort from "@server/requests/elevate/mapNgSort";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import type { Sort } from "types/common";

import { PRODUCTS_PER_PAGE } from "@/config/products";
import { generateHash, getInitialParams } from "@/utils/productSearch";

const useProductListing = ({
  initialData,
  pageNumber = 1,
  params,
  body,
}: {
  initialData?: any;
  pageNumber?: number;
  params: LandingPageParams;
  body?: LandingPageBody;
}) => {
  const router = useRouter();
  const market = useMarket();
  const [state, setState] = useState<any | undefined>();
  const [data, setData] = useState<
    Awaited<ReturnType<typeof getLandingPage>> | undefined
  >(initialData);

  const getCategoryPayload = useCallback(() => {
    const { sort, filters } = getInitialParams(initialData);
    return {
      ...params,
      sort: mapNgSort(sort),
      limit: PRODUCTS_PER_PAGE,
      skip: PRODUCTS_PER_PAGE * (pageNumber - 1),
      facets: { ...params.facets, ...mapNgFilters(filters) },
    };
  }, [initialData, pageNumber, params]);

  const onChange = useCallback(() => {
    const { sort, filters } = getInitialParams(initialData);
    const payload = getCategoryPayload();

    getLandingPage(market, payload as LandingPageParams, body)
      .then(setData)
      .catch(() => console.error("Failed to fetch category"));

    setState({ sort, filters });

    setState({ sort, filters });
  }, [initialData, getCategoryPayload, market, body]);

  const updateFilter = (payload: any[]) => {
    const { sort } = getInitialParams(initialData);

    if (pageNumber && pageNumber > 1) {
      router.push(generateHash(sort, payload));
    } else {
      window.history.replaceState(
        window.history.state,
        "",
        generateHash(sort, payload),
      );
      onChange();
    }
  };

  const updateSort = (payload: Sort) => {
    const { filters } = getInitialParams(initialData);

    if (pageNumber && pageNumber > 1) {
      router.push(generateHash(payload, filters));
    } else {
      window.history.replaceState(
        window.history.state,
        "",
        generateHash(payload, filters),
      );
      onChange();
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash.length) {
      onChange();
    }
  }, [onChange, initialData]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const payload = getCategoryPayload();

    void getLandingPage(market, payload as LandingPageParams, body);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    state,
    updateSort,
    updateFilter,
  };
};

export default useProductListing;
